// Estructura de la ficha clínica. La especialidad se elige en brand.js (BRAND.specialty).
import { BRAND } from "./brand";

export const SEXO = ["Femenino", "Masculino", "Otro"];
export const SANGRE = ["O+", "O-", "A+", "A-", "B+", "B-", "AB+", "AB-"];
export const ESTADO_CIVIL = ["Soltero(a)", "Casado(a)", "Unión libre", "Divorciado(a)", "Viudo(a)"];

// type: "area" (texto largo) | "text"; full: ocupa el ancho completo
const SPECS = {
  cardio: {
    chronic: { label: "Enfermedades crónicas", hint: "Control cada 3 meses: el paciente recibe un recordatorio automático 7 días antes." },
    sections: [
      { title: "Antecedentes y hábitos", icon: "stetho", followup: true, fields: [
        ["motivo_control", "Motivo del control", "area"], ["factores_riesgo", "Factores de riesgo", "area"],
        ["alergias", "Alergias", "area"], ["habitos", "Hábitos", "area"],
        ["app", "APP (antecedentes patológicos personales)", "area"], ["apqx", "APQX (antecedentes quirúrgicos)", "area"],
        ["medicacion", "Medicación", "area"], ["actividad_fisica", "Actividad física", "text"], ["vacuna_covid", "Vacuna Covid", "text"],
      ] },
      { title: "Examen físico y signos", icon: "heart", followup: true, fields: [
        ["cuadro_clinico", "Cuadro clínico", "area", true], ["ex_fisico_respiratorio", "Ex. físico — Respiratorio", "area"],
        ["ex_fisico_cardiovascular", "Ex. físico — Cardiovascular", "area"], ["igy", "IGY", "text"],
        ["sv", "SV (signos vitales)", "text"], ["imc", "IMC", "text"], ["ecg_reposo", "ECG en reposo", "text"],
      ] },
      { title: "Visor clínico: diagnóstico, laboratorios, EKG y eco", icon: "visor", fields: [
        ["titulo_diagnostico", "Título / diagnóstico", "text", true], ["laboratorios", "Laboratorios (resultados)", "area"],
        ["eco_descripcion", "Ecocardiograma (descripción)", "area"],
      ] },
    ],
    media: {
      lab_media: { label: "Laboratorios — PDF o imágenes", accept: "application/pdf,image/*" },
      ekg_media: { label: "EKG — imágenes", accept: "image/*" },
      eco_media: { label: "Ecocardiograma — imágenes o video", accept: "image/*,video/*" },
    },
    visor: { images: "EKG", video: "Ecocardiograma", videoText: "eco_descripcion", labs: "Laboratorios",
      signs: [["sv", "SV"], ["imc", "IMC"], ["ecg_reposo", "ECG reposo:"]] },
    cases: { intro: "Presenta casos clínicos con su cuadro, EKG, laboratorios y ecocardiograma en formato de diapositiva.",
      images: "EKG", video: "Eco", videoText: "Ecocardiograma (mediciones)" },
  },
  gastro: {
    chronic: { label: "Paciente en control periódico", hint: "Hepatitis, cirrosis, EII, programa de peso, etc.: control cada 3 meses con recordatorio automático 7 días antes." },
    sections: [
      { title: "Motivo de consulta y antecedentes", icon: "stetho", followup: true, fields: [
        ["motivo_control", "Motivo de consulta", "area"], ["sintomas", "Síntomas digestivos", "area"],
        ["factores_riesgo", "Factores de riesgo", "area"], ["antecedentes_familiares", "Antecedentes familiares (cáncer digestivo, etc.)", "area"],
        ["app", "APP (antecedentes patológicos personales)", "area"], ["apqx", "APQX (antecedentes quirúrgicos)", "area"],
        ["alergias", "Alergias", "area"], ["habitos", "Hábitos (alcohol, tabaco, dieta)", "area"],
        ["medicacion", "Medicación", "area", true],
      ] },
      { title: "Examen físico y medidas", icon: "heart", followup: true, fields: [
        ["cuadro_clinico", "Cuadro clínico / enfermedad actual", "area", true], ["ex_abdominal", "Examen abdominal", "area", true],
        ["peso", "Peso (kg)", "text"], ["talla", "Talla (cm)", "text"], ["imc", "IMC", "text"],
        ["perimetro_abdominal", "Perímetro abdominal (cm)", "text"], ["sv", "SV (signos vitales)", "text"],
      ] },
      { title: "Estudios y procedimientos", icon: "visor", followup: true, fields: [
        ["endoscopia_alta", "Endoscopía digestiva alta — hallazgos", "area"], ["colonoscopia", "Colonoscopía — hallazgos", "area"],
        ["cpre", "CPRE — hallazgos", "area"], ["patologia", "Biopsia / patología", "area"],
        ["h_pylori", "H. pylori", "text"], ["eco_abdominal", "Ecografía / imágenes abdominales", "area"],
        ["laboratorios", "Laboratorios (resultados)", "area", true],
      ] },
      { title: "Diagnóstico y plan", icon: "visor", followup: true, fields: [
        ["titulo_diagnostico", "Diagnóstico", "text", true], ["plan_tratamiento", "Plan / tratamiento", "area", true],
      ] },
    ],
    media: {
      ekg_media: { label: "Imágenes de endoscopía / colonoscopía", accept: "image/*" },
      eco_media: { label: "Videos de procedimientos", accept: "video/*,image/*" },
      lab_media: { label: "Laboratorios, patología e informes — PDF", accept: "application/pdf,image/*" },
    },
    visor: { images: "Imágenes endoscópicas", video: "Procedimiento", diagnosis: "Plan / tratamiento", videoText: "endoscopia_alta", videoText2: "colonoscopia", labs: "Laboratorios y patología",
      signs: [["peso", "Peso"], ["imc", "IMC"], ["h_pylori", "H. pylori:"]] },
    cases: { intro: "Presenta casos clínicos con su cuadro, imágenes endoscópicas, patología y videos de procedimientos en formato de diapositiva.",
      images: "Imágenes", video: "Video", videoText: "Hallazgos del procedimiento" },
  },
};

export const SPEC = SPECS[BRAND.specialty] || SPECS.cardio;

const COMMON_EMPTY = {
  nombre: "", sexo: "", fecha_nacimiento: "", edad: "", tipo_sangre: "", cedula: "", telefono: "", email: "",
  ciudad: "", direccion: "", estado_civil: "", ocupacion: "", seguro: "", contacto_emergencia: "", telefono_emergencia: "",
  fecha_consulta: "", consulta_lugar: "", enfermedades_cronicas: false, notas: "",
  ekg_media: [], eco_media: [], lab_media: [],
};
export const EMPTY_PATIENT = SPEC.sections.reduce((acc, s) => { s.fields.forEach(([k]) => { acc[k] = ""; }); return acc; }, { ...COMMON_EMPTY });

export const FOLLOWUP_GROUPS = SPEC.sections.filter((s) => s.followup).map((s) => ({
  group: s.title, items: s.fields.filter(([k]) => k !== "titulo_diagnostico" || BRAND.specialty === "gastro").map(([k, l]) => [k, l]),
}));

export const DETAIL_GROUPS = [
  {
    group: "Datos personales",
    items: [
      ["cedula", "Cédula"], ["sexo", "Sexo"], ["fecha_nacimiento", "Fecha de nacimiento"], ["edad", "Edad"],
      ["tipo_sangre", "Tipo de sangre"], ["estado_civil", "Estado civil"], ["ocupacion", "Ocupación"],
      ["telefono", "Teléfono / WhatsApp"], ["email", "Email"], ["ciudad", "Ciudad"], ["direccion", "Dirección"],
      ["seguro", "Seguro médico"], ["contacto_emergencia", "Contacto de emergencia"], ["telefono_emergencia", "Teléfono de emergencia"],
      ["fecha_consulta", "Primera consulta"], ["consulta_lugar", "Lugar de consulta"],
    ],
  },
  ...SPEC.sections.map((s) => ({ group: s.title.replace(/^Visor clínico.*/, "Visor clínico"), items: s.fields.map(([k, l]) => [k, l]) })),
  { group: "Notas", items: [["notas", "Notas internas"]] },
];
